import { Form, FormSubmit, useStoreState } from "@ariakit/react";
import {
  Failure,
  LetterheadTextField,
  MiddotSeparated,
  Success,
  useForm,
} from "@indietabletop/appkit";
import { groupBy } from "es-toolkit";
import type { Greathelm } from "../../types/lib/index.ts";
import { button } from "../common.css";
import { database } from "../database/database";
import {
  DialogActions,
  DialogContent,
  DialogHeader,
  ScrollableDialog,
} from "../Dialog/Dialog";
import type { ItemProfile } from "../engine/ItemProfile.ts";
import type { DefaultKnight } from "../engine/Ruleset";
import type { WeaponProfile } from "../engine/WeaponProfile.ts";
import { itemType, weaponType } from "../fmt";
import { Divider, FormOption, FormOptionsGroup } from "../forms/FormTextField";
import {
  useHandleSuccess,
  type SuccessReaction,
} from "../KnightlyOrdersPage/useHandleSuccess";
import {
  CoreRulesText,
  PremiumContentDividerNote,
} from "../PremiumContent/PremiumContent";
import { useIsFeatureUnlocked } from "../PremiumContent/useIsFeatureUnlocked";
import { useKnight } from "./KnightContext";
import { useWarbandContext } from "./useWarbandContext.ts";

type WeaponProfileGroups = Partial<
  Record<WeaponProfile["type"], WeaponProfile[]>
>;

type ItemProfileGroups = Partial<Record<ItemProfile["type"], ItemProfile[]>>;

type FormValues = {
  name: string;
  weaponId: string;
  shieldId: string;
  itemId: string;
  wearableId: string;
  armorId: string;
};

function truthyToRefs(id: string) {
  return id ? [{ id }] : [];
}

function toKnightData(values: FormValues): Partial<Greathelm.KnightData> {
  return {
    name: values.name,
    weapons: truthyToRefs(values.weaponId),
    shields: truthyToRefs(values.shieldId),
    items: truthyToRefs(values.itemId),
    wearables: truthyToRefs(values.wearableId),
    armor: { id: values.armorId },
  };
}

function KnightFormDialog(props: {
  heading: string;
  submitLabel: string;
  defaultValues: DefaultKnight;
  onSubmit: (
    values: Partial<Greathelm.KnightData>,
  ) => Promise<Success<SuccessReaction> | Failure<string>>;
  backTo: string;
}) {
  const { heading, submitLabel, defaultValues, backTo, onSubmit } = props;

  const handleSuccess = useHandleSuccess();
  const canAccessCoreRules = useIsFeatureUnlocked("GREATHELM_CORE_RULES");
  const warband = useWarbandContext();

  const {
    weaponProfiles,
    shieldProfiles,
    armorProfiles,
    itemProfiles,
    wearableProfiles,
  } = warband.ruleset;

  const formValues: FormValues = {
    name: defaultValues.name,
    weaponId: defaultValues.weapons.at(0)?.id ?? "",
    shieldId: defaultValues.shields.at(0)?.id ?? "",
    itemId: defaultValues.items.at(0)?.id ?? "",
    wearableId: defaultValues.wearables.at(0)?.id ?? "",
    armorId: defaultValues.armor.id,
  };

  const { form } = useForm({
    defaultValues: formValues,
    async onSubmit({ values }) {
      return await onSubmit(toKnightData(values));
    },
    onSuccess: handleSuccess,
  });

  const weapons: WeaponProfileGroups = groupBy(
    weaponProfiles.toArray(),
    (w) => w.type,
  );

  const items: ItemProfileGroups = groupBy(
    itemProfiles.toArray(),
    (i) => i.type,
  );

  // When picking two-handed weapons, we want to automatically unselect
  // the shield to save the user some clicking.
  const clearShieldSelection = () => {
    form.setValue(form.names.shieldId, "");
  };

  const values = useStoreState(form, (s) => s.values);

  return (
    <ScrollableDialog>
      <Form store={form}>
        <DialogHeader heading={heading} />

        <DialogContent>
          <p
            style={{
              textAlign: "center",
              fontSize: "1.125rem",
              lineHeight: 1.5,
              marginBlockEnd: "3rem",
            }}
          >
            A knight may take a one-handed weapon and a shield, or a two-handed
            weapon. They must pick one armor type.
          </p>

          <div
            style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
          >
            <LetterheadTextField
              name={form.names.name}
              label="Name"
              placeholder="E.g. Royal Pikeman"
            />

            <FormOptionsGroup label="Weapons">
              {weapons.ONE_HANDED?.map((weapon) => {
                return (
                  <FormOption
                    key={weapon.id}
                    type="radio"
                    name={form.names.weaponId}
                    label={weapon.name}
                    value={weapon.id}
                    description={
                      <MiddotSeparated>
                        <em>{weaponType(weapon.type)}</em>
                        {weapon.summary}
                      </MiddotSeparated>
                    }
                  />
                );
              })}

              <Divider />

              {weapons.TWO_HANDED?.map((weapon) => {
                return (
                  <FormOption
                    key={weapon.id}
                    type="radio"
                    name={form.names.weaponId}
                    label={weapon.name}
                    value={weapon.id}
                    onChange={clearShieldSelection}
                    description={
                      <MiddotSeparated>
                        <em>{weaponType(weapon.type)}</em>
                        {weapon.summary}
                      </MiddotSeparated>
                    }
                  />
                );
              })}

              <Divider />

              {weapons.TWO_HANDED_RANGED?.map((weapon) => {
                return (
                  <FormOption
                    key={weapon.id}
                    type="radio"
                    name={form.names.weaponId}
                    label={weapon.name}
                    value={weapon.id}
                    onChange={clearShieldSelection}
                    description={
                      <MiddotSeparated>
                        <em>{weaponType(weapon.type)}</em>
                        {weapon.summary}
                      </MiddotSeparated>
                    }
                  />
                );
              })}
            </FormOptionsGroup>

            {!canAccessCoreRules && (
              <PremiumContentDividerNote backTo={backTo} />
            )}

            <FormOptionsGroup label="Shields">
              {shieldProfiles.toArray().map((shield) => {
                return (
                  <FormOption
                    key={shield.id}
                    type="checkbox"
                    name={form.names.shieldId}
                    label={shield.name}
                    value={shield.id}
                    disabled={!canAccessCoreRules}
                    description={
                      shield.id === values.shieldId ? (
                        <span>{shield.summary}</span>
                      ) : (
                        <CoreRulesText>{shield.summary}</CoreRulesText>
                      )
                    }
                  />
                );
              })}
            </FormOptionsGroup>

            <FormOptionsGroup label="Armor">
              {armorProfiles.toArray().map((armor) => {
                return (
                  <FormOption
                    key={armor.id}
                    type="radio"
                    name={form.names.armorId}
                    label={armor.name}
                    value={armor.id}
                    disabled={!canAccessCoreRules}
                    description={
                      armor.id === values.armorId ? (
                        <span>{armor.summary}</span>
                      ) : (
                        <CoreRulesText>{armor.summary}</CoreRulesText>
                      )
                    }
                  />
                );
              })}
            </FormOptionsGroup>

            <FormOptionsGroup label="Worn Items">
              {wearableProfiles.toArray().map((item) => {
                return (
                  <FormOption
                    key={item.id}
                    type="checkbox"
                    name={form.names.wearableId}
                    label={item.name}
                    value={item.id}
                    disabled={!canAccessCoreRules}
                    description={<CoreRulesText>{item.summary}</CoreRulesText>}
                  />
                );
              })}
            </FormOptionsGroup>

            <FormOptionsGroup label="Use Items">
              {items.USABLE?.map((item) => {
                return (
                  <FormOption
                    key={item.id}
                    type="checkbox"
                    name={form.names.itemId}
                    label={item.name}
                    value={item.id}
                    disabled={!canAccessCoreRules}
                    description={
                      <MiddotSeparated>
                        <em>{itemType(item.type)}</em>
                        <CoreRulesText>{item.summary}</CoreRulesText>
                      </MiddotSeparated>
                    }
                  />
                );
              })}
            </FormOptionsGroup>

            <FormOptionsGroup label="Consumable Items">
              {items.CONSUMABLE_INSTANT?.map((item) => {
                return (
                  <FormOption
                    key={item.id}
                    type="checkbox"
                    name={form.names.itemId}
                    label={item.name}
                    value={item.id}
                    disabled={!canAccessCoreRules}
                    description={
                      <MiddotSeparated>
                        <em>{itemType(item.type)}</em>
                        <CoreRulesText>{item.summary}</CoreRulesText>
                      </MiddotSeparated>
                    }
                  />
                );
              })}

              <Divider />

              {items.CONSUMABLE_BUFF?.map((item) => {
                return (
                  <FormOption
                    key={item.id}
                    type="checkbox"
                    name={form.names.itemId}
                    label={item.name}
                    value={item.id}
                    disabled={!canAccessCoreRules}
                    description={
                      <MiddotSeparated>
                        <em>{itemType(item.type)}</em>
                        <CoreRulesText>{item.summary}</CoreRulesText>
                      </MiddotSeparated>
                    }
                  />
                );
              })}

              <Divider />

              {items.CONSUMABLE_TOSSED?.map((item) => {
                return (
                  <FormOption
                    key={item.id}
                    type="checkbox"
                    name={form.names.itemId}
                    label={item.name}
                    value={item.id}
                    disabled={!canAccessCoreRules}
                    description={
                      <MiddotSeparated>
                        <em>{itemType(item.type)}</em>
                        <CoreRulesText>{item.summary}</CoreRulesText>
                      </MiddotSeparated>
                    }
                  />
                );
              })}
            </FormOptionsGroup>
          </div>
        </DialogContent>

        <DialogActions>
          <FormSubmit className={button()}>{submitLabel}</FormSubmit>
        </DialogActions>
      </Form>
    </ScrollableDialog>
  );
}

export function AddKnightDialog(props: { backTo: string }) {
  const warband = useWarbandContext();

  return (
    <KnightFormDialog
      {...props}
      heading="Add Knight"
      submitLabel="Add"
      defaultValues={warband.ruleset.defaultKnight}
      onSubmit={async (values) => {
        const knight = warband.addKnight(values);
        const result = await database.warbands.updateOne(knight.warband.data);
        return result.mapSuccess(() => ({ navigateTo: `/k/${knight.id}` }));
      }}
    />
  );
}

export function EditKnightDialog(props: { backTo: string }) {
  const knight = useKnight();

  return (
    <KnightFormDialog
      {...props}
      heading="Edit Knight"
      submitLabel="Save"
      defaultValues={knight.data}
      onSubmit={async (values) => {
        const next = knight.clone(values);
        const result = await database.warbands.updateOne(next.warband.data);
        return result.mapSuccess(() => ({ message: "Updated" }));
      }}
    />
  );
}
